import { useEffect, useMemo, useState } from "react"
import { ToastMessage, useToast } from "@stayframe/ui"
import { formatApiError } from "@/lib/api"
import {
  opsAdminApi,
  type LlmRoutingCategory,
  type OpsLlmRoutingResponse,
  type OpsLlmRoutingSurface,
} from "@/lib/opsAdminApi"

type RouteDraft = {
  primary_provider: string
  primary_model: string
  fallback_provider: string
  fallback_model: string
}

const CATEGORY_TABS: Array<{ key: LlmRoutingCategory; label: string }> = [
  { key: "guest", label: "Guest" },
  { key: "host", label: "Host" },
  { key: "ops", label: "Ops" },
]

function firstModel(providerModels: Record<string, string[]>, provider: string) {
  const models = providerModels[provider] || []
  return models[0] || ""
}

function buildDraftFromSurface(surface: OpsLlmRoutingSurface): RouteDraft {
  return {
    primary_provider: surface.resolved.primary.provider,
    primary_model: surface.resolved.primary.model,
    fallback_provider: surface.resolved.fallback.provider,
    fallback_model: surface.resolved.fallback.model,
  }
}

function isSameRoute(a: RouteDraft, b: RouteDraft) {
  return (
    a.primary_provider === b.primary_provider
    && a.primary_model === b.primary_model
    && a.fallback_provider === b.fallback_provider
    && a.fallback_model === b.fallback_model
  )
}

export function OpsAiRoutingPage() {
  const { toast } = useToast()

  const [properties, setProperties] = useState<Array<{ id: string; name: string }>>([])
  const [propertyId, setPropertyId] = useState("")
  const [loading, setLoading] = useState(true)
  const [savingMap, setSavingMap] = useState<Record<string, boolean>>({})

  const [routing, setRouting] = useState<OpsLlmRoutingResponse | null>(null)
  const [drafts, setDrafts] = useState<Record<string, RouteDraft>>({})

  const [activeTab, setActiveTab] = useState<LlmRoutingCategory>("guest")
  const [search, setSearch] = useState("")

  const [bulkPrimaryProvider, setBulkPrimaryProvider] = useState<Record<LlmRoutingCategory, string>>({ guest: "", host: "", ops: "" })
  const [bulkPrimaryModel, setBulkPrimaryModel] = useState<Record<LlmRoutingCategory, string>>({ guest: "", host: "", ops: "" })
  const [bulkFallbackProvider, setBulkFallbackProvider] = useState<Record<LlmRoutingCategory, string>>({ guest: "", host: "", ops: "" })
  const [bulkFallbackModel, setBulkFallbackModel] = useState<Record<LlmRoutingCategory, string>>({ guest: "", host: "", ops: "" })

  const providerModels = routing?.provider_models || {}
  const surfaces = routing?.surfaces || []
  const categories = routing?.categories

  function showApiError(result: Record<string, unknown>, fallback = "Request failed.") {
    const err = formatApiError(result)
    toast(<ToastMessage message={err.message || fallback} requestId={err.requestId} />, "error")
  }

  async function loadProperties() {
    const res = await opsAdminApi.getProperties("all")
    if (!res.ok || !Array.isArray(res.properties)) {
      showApiError(res as unknown as Record<string, unknown>, "Failed to load properties.")
      return
    }

    const items = res.properties
      .map((row) => ({
        id: String(row.property_id || ""),
        name: String(row.property_name || row.property_id || ""),
      }))
      .filter((row) => row.id)

    setProperties(items)
    if (!propertyId && items[0]?.id) {
      setPropertyId(items[0].id)
    }
  }

  function resetBulkSelectors(next: OpsLlmRoutingResponse) {
    const nextCategories = next.categories
    if (!nextCategories) return
    const nextPrimaryProvider = { ...bulkPrimaryProvider }
    const nextPrimaryModel = { ...bulkPrimaryModel }
    const nextFallbackProvider = { ...bulkFallbackProvider }
    const nextFallbackModel = { ...bulkFallbackModel }

    for (const category of CATEGORY_TABS.map((item) => item.key)) {
      const resolved = nextCategories[category]?.resolved
      if (!resolved) continue
      nextPrimaryProvider[category] = resolved.primary.provider
      nextPrimaryModel[category] = resolved.primary.model
      nextFallbackProvider[category] = resolved.fallback.provider
      nextFallbackModel[category] = resolved.fallback.model
    }

    setBulkPrimaryProvider(nextPrimaryProvider)
    setBulkPrimaryModel(nextPrimaryModel)
    setBulkFallbackProvider(nextFallbackProvider)
    setBulkFallbackModel(nextFallbackModel)
  }

  async function loadRouting(nextPropertyId: string) {
    if (!nextPropertyId) return
    setLoading(true)
    const res = await opsAdminApi.getLlmRouting(nextPropertyId)
    if (!res.ok) {
      setRouting(null)
      setDrafts({})
      showApiError(res as unknown as Record<string, unknown>, "Failed to load AI routing.")
      setLoading(false)
      return
    }

    setRouting(res)
    const nextDrafts: Record<string, RouteDraft> = {}
    for (const surface of res.surfaces || []) {
      nextDrafts[surface.surface_key] = buildDraftFromSurface(surface)
    }
    setDrafts(nextDrafts)
    resetBulkSelectors(res)
    setLoading(false)
  }

  useEffect(() => {
    loadProperties().catch(() => {})
  }, [])

  useEffect(() => {
    if (!propertyId) return
    loadRouting(propertyId).catch(() => {})
  }, [propertyId])

  const filteredSurfaces = useMemo(() => {
    const query = search.trim().toLowerCase()
    return surfaces.filter((surface) => {
      if (surface.category !== activeTab) return false
      if (!query) return true
      const haystack = `${surface.surface_key} ${surface.name} ${surface.description}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [surfaces, activeTab, search])

  function categoryKillSwitch(category: LlmRoutingCategory) {
    return Boolean(categories?.[category]?.resolved?.kill_switch)
  }

  function rowValidation(surface: OpsLlmRoutingSurface, draft: RouteDraft) {
    if (!draft.primary_provider || !draft.primary_model) return "Primary route is required."
    if (!draft.fallback_provider || !draft.fallback_model) return "Fallback route is required."
    if (!categoryKillSwitch(surface.category) && draft.primary_provider === draft.fallback_provider && draft.primary_model === draft.fallback_model) {
      return "Primary and fallback must differ when kill switch is off."
    }
    return ""
  }

  function updateDraft(surfaceKey: string, patch: Partial<RouteDraft>) {
    setDrafts((prev) => ({
      ...prev,
      [surfaceKey]: {
        ...prev[surfaceKey],
        ...patch,
      },
    }))
  }

  async function saveSurface(surface: OpsLlmRoutingSurface, draft: RouteDraft) {
    const validationError = rowValidation(surface, draft)
    if (validationError) {
      toast(validationError, "error")
      return
    }

    setSavingMap((prev) => ({ ...prev, [surface.surface_key]: true }))
    const res = await opsAdminApi.saveLlmRouting({
      property_id: propertyId,
      updates: [{
        surface_key: surface.surface_key,
        primary_provider: draft.primary_provider,
        primary_model: draft.primary_model,
        fallback_provider: draft.fallback_provider,
        fallback_model: draft.fallback_model,
      }],
    })
    setSavingMap((prev) => ({ ...prev, [surface.surface_key]: false }))

    if (!res.ok) {
      showApiError(res as unknown as Record<string, unknown>, "Failed to save surface route.")
      return
    }

    setRouting(res)
    const nextDrafts: Record<string, RouteDraft> = {}
    for (const item of res.surfaces || []) nextDrafts[item.surface_key] = buildDraftFromSurface(item)
    setDrafts(nextDrafts)
    resetBulkSelectors(res)
    toast("Surface route saved.", "success")
  }

  async function resetSurface(surface: OpsLlmRoutingSurface) {
    const defaults: RouteDraft = {
      primary_provider: surface.default_primary.provider,
      primary_model: surface.default_primary.model,
      fallback_provider: surface.default_fallback.provider,
      fallback_model: surface.default_fallback.model,
    }
    updateDraft(surface.surface_key, defaults)
    await saveSurface(surface, defaults)
  }

  async function applyBulk(category: LlmRoutingCategory, type: "primary" | "fallback") {
    const provider = type === "primary" ? bulkPrimaryProvider[category] : bulkFallbackProvider[category]
    const model = type === "primary" ? bulkPrimaryModel[category] : bulkFallbackModel[category]
    if (!provider || !model) {
      toast("Select provider and model first.", "error")
      return
    }

    setSavingMap((prev) => ({ ...prev, [`bulk:${category}:${type}`]: true }))
    const payload = type === "primary"
      ? { category, primary_provider: provider, primary_model: model }
      : { category, fallback_provider: provider, fallback_model: model }

    const res = await opsAdminApi.saveLlmRouting({
      property_id: propertyId,
      category_updates: [payload],
    })
    setSavingMap((prev) => ({ ...prev, [`bulk:${category}:${type}`]: false }))

    if (!res.ok) {
      showApiError(res as unknown as Record<string, unknown>, `Failed to apply ${type} route.`)
      return
    }

    setRouting(res)
    const nextDrafts: Record<string, RouteDraft> = {}
    for (const item of res.surfaces || []) nextDrafts[item.surface_key] = buildDraftFromSurface(item)
    setDrafts(nextDrafts)
    resetBulkSelectors(res)
    toast(`Category ${type} applied.`, "success")
  }

  async function toggleKillSwitch(category: LlmRoutingCategory, enabled: boolean) {
    setSavingMap((prev) => ({ ...prev, [`kill:${category}`]: true }))
    const res = await opsAdminApi.saveLlmRouting({
      property_id: propertyId,
      kill_switch_updates: [{ category, kill_switch: enabled }],
    })
    setSavingMap((prev) => ({ ...prev, [`kill:${category}`]: false }))

    if (!res.ok) {
      showApiError(res as unknown as Record<string, unknown>, "Failed to update kill switch.")
      return
    }

    setRouting(res)
    const nextDrafts: Record<string, RouteDraft> = {}
    for (const item of res.surfaces || []) nextDrafts[item.surface_key] = buildDraftFromSurface(item)
    setDrafts(nextDrafts)
    resetBulkSelectors(res)
    toast(`Kill switch ${enabled ? "enabled" : "disabled"}.`, "success")
  }

  return (
    <div className="ops-admin-page">
      <div className="page-header ops-page-header">
        <div className="ops-page-header__row">
          <h1 className="page-header__title" style={{ marginBottom: 0 }}>AI Routing</h1>
          <span className="ops-badge">Ops</span>
        </div>
        <p className="page-header__subtitle">
          Configure provider and model routing per surface with per-property overrides.
        </p>
      </div>

      <section className="ops-ai-toolbar">
        <label className="ops-ai-toolbar__field">
          <span className="ops-ai-toolbar__label">Property</span>
          <select
            className="sf-field-select"
            value={propertyId}
            onChange={(event) => setPropertyId(event.target.value)}
          >
            {properties.map((property) => (
              <option key={property.id} value={property.id}>{property.name}</option>
            ))}
          </select>
        </label>
        <label className="ops-ai-toolbar__field">
          <span className="ops-ai-toolbar__label">Search surfaces</span>
          <input
            className="sf-field-select"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by key, name or description"
          />
        </label>
      </section>

      <section className="ops-summary-grid">
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Surfaces</div>
          <div className="ops-summary-card__line">{routing?.summary?.total_surfaces ?? 0}</div>
        </div>
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Surface Overrides</div>
          <div className="ops-summary-card__line">{routing?.summary?.overridden_surfaces ?? 0}</div>
        </div>
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Category Overrides</div>
          <div className="ops-summary-card__line">{routing?.summary?.category_overrides ?? 0}</div>
        </div>
        <div className="ops-summary-card">
          <div className="ops-summary-card__title">Kill Switches</div>
          <div className="ops-summary-card__line">
            {CATEGORY_TABS.map((item) => `${item.label}: ${routing?.summary?.kill_switches?.[item.key] ? "on" : "off"}`).join(" · ")}
          </div>
        </div>
      </section>

      <section className="ops-chip-row">
        {CATEGORY_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ops-filter-chip${tab.key === activeTab ? " ops-filter-chip--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </section>

      <section className="ops-ai-category-controls">
        <div className="ops-ai-category-controls__row">
          <strong>{CATEGORY_TABS.find((item) => item.key === activeTab)?.label} controls</strong>
          <button
            type="button"
            className="data-table__action"
            onClick={() => toggleKillSwitch(activeTab, !categoryKillSwitch(activeTab))}
            disabled={Boolean(savingMap[`kill:${activeTab}`]) || loading}
          >
            Kill switch: {categoryKillSwitch(activeTab) ? "On" : "Off"}
          </button>
        </div>

        <div className="ops-ai-category-controls__grid">
          <div className="ops-ai-bulk-card">
            <div className="ops-ai-bulk-card__title">Apply primary to all</div>
            <div className="ops-ai-bulk-card__fields">
              <select
                className="sf-field-select"
                value={bulkPrimaryProvider[activeTab] || ""}
                onChange={(event) => {
                  const provider = event.target.value
                  setBulkPrimaryProvider((prev) => ({ ...prev, [activeTab]: provider }))
                  setBulkPrimaryModel((prev) => ({ ...prev, [activeTab]: firstModel(providerModels, provider) }))
                }}
              >
                <option value="">Provider</option>
                {Object.keys(providerModels).map((provider) => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
              <select
                className="sf-field-select"
                value={bulkPrimaryModel[activeTab] || ""}
                onChange={(event) => setBulkPrimaryModel((prev) => ({ ...prev, [activeTab]: event.target.value }))}
              >
                <option value="">Model</option>
                {(providerModels[bulkPrimaryProvider[activeTab]] || []).map((model) => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
              <button
                type="button"
                className="data-table__action"
                onClick={() => applyBulk(activeTab, "primary")}
                disabled={Boolean(savingMap[`bulk:${activeTab}:primary`]) || loading}
              >
                Apply
              </button>
            </div>
          </div>

          <div className="ops-ai-bulk-card">
            <div className="ops-ai-bulk-card__title">Apply fallback to all</div>
            <div className="ops-ai-bulk-card__fields">
              <select
                className="sf-field-select"
                value={bulkFallbackProvider[activeTab] || ""}
                onChange={(event) => {
                  const provider = event.target.value
                  setBulkFallbackProvider((prev) => ({ ...prev, [activeTab]: provider }))
                  setBulkFallbackModel((prev) => ({ ...prev, [activeTab]: firstModel(providerModels, provider) }))
                }}
              >
                <option value="">Provider</option>
                {Object.keys(providerModels).map((provider) => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
              <select
                className="sf-field-select"
                value={bulkFallbackModel[activeTab] || ""}
                onChange={(event) => setBulkFallbackModel((prev) => ({ ...prev, [activeTab]: event.target.value }))}
              >
                <option value="">Model</option>
                {(providerModels[bulkFallbackProvider[activeTab]] || []).map((model) => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
              <button
                type="button"
                className="data-table__action"
                onClick={() => applyBulk(activeTab, "fallback")}
                disabled={Boolean(savingMap[`bulk:${activeTab}:fallback`]) || loading}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="ops-ai-surface-list">
        {loading ? (
          <div className="ops-llm-empty">Loading AI routing...</div>
        ) : filteredSurfaces.length === 0 ? (
          <div className="ops-llm-empty">No surfaces match this filter.</div>
        ) : filteredSurfaces.map((surface) => {
          const draft = drafts[surface.surface_key] || buildDraftFromSurface(surface)
          const validationError = rowValidation(surface, draft)
          const unsaved = !isSameRoute(draft, buildDraftFromSurface(surface))
          return (
            <article key={surface.surface_key} className="ops-ai-surface-row">
              <div className="ops-ai-surface-row__meta">
                <div className="ops-ai-surface-row__title">{surface.name}</div>
                <div className="ops-ai-surface-row__key">{surface.surface_key}</div>
                <p className="ops-ai-surface-row__desc">{surface.description}</p>
                <div className="ops-ai-surface-row__reason">Resolved from: {surface.resolved.reason.replace(/_/g, " ")}</div>
                {unsaved && <div className="ops-ai-surface-row__unsaved">Unsaved changes</div>}
                {validationError && <div className="ops-ai-surface-row__error">{validationError}</div>}
              </div>

              <div className="ops-ai-surface-row__controls">
                <label>
                  <span>Primary provider</span>
                  <select
                    className="sf-field-select"
                    value={draft.primary_provider}
                    onChange={(event) => {
                      const provider = event.target.value
                      updateDraft(surface.surface_key, {
                        primary_provider: provider,
                        primary_model: firstModel(providerModels, provider),
                      })
                    }}
                  >
                    {Object.keys(providerModels).map((provider) => (
                      <option key={provider} value={provider}>{provider}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Primary model</span>
                  <select
                    className="sf-field-select"
                    value={draft.primary_model}
                    onChange={(event) => updateDraft(surface.surface_key, { primary_model: event.target.value })}
                  >
                    {(providerModels[draft.primary_provider] || []).map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Fallback provider</span>
                  <select
                    className="sf-field-select"
                    value={draft.fallback_provider}
                    onChange={(event) => {
                      const provider = event.target.value
                      updateDraft(surface.surface_key, {
                        fallback_provider: provider,
                        fallback_model: firstModel(providerModels, provider),
                      })
                    }}
                  >
                    {Object.keys(providerModels).map((provider) => (
                      <option key={provider} value={provider}>{provider}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Fallback model</span>
                  <select
                    className="sf-field-select"
                    value={draft.fallback_model}
                    onChange={(event) => updateDraft(surface.surface_key, { fallback_model: event.target.value })}
                  >
                    {(providerModels[draft.fallback_provider] || []).map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </label>
                <div className="ops-ai-surface-row__actions">
                  <button
                    type="button"
                    className="data-table__action"
                    onClick={() => saveSurface(surface, draft)}
                    disabled={Boolean(savingMap[surface.surface_key]) || Boolean(validationError)}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="data-table__action"
                    onClick={() => resetSurface(surface).catch(() => {})}
                    disabled={Boolean(savingMap[surface.surface_key])}
                  >
                    Reset to defaults
                  </button>
                </div>
              </div>
            </article>
          )
        })}
      </section>

      <section className="ops-ai-needs-review">
        <h2 className="ops-drawer-section__title">Needs review</h2>
        {(!routing?.needs_review || routing.needs_review.length === 0) ? (
          <p className="ops-llm-empty">No unresolved surface entries.</p>
        ) : (
          <div className="ops-ai-needs-review__list">
            {routing.needs_review.map((item) => (
              <div key={`${item.key}:${item.category}`} className="ops-ai-needs-review__item">
                <strong>{item.key}</strong>
                <span>{item.description}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
